import { SubscriptionTier } from '@/types';

// Re-export from the new component for backward compatibility
export const ATTACK_SURFACE_OPTIONS = [
  {
    label: 'External',
    value: 'external',
  },
  {
    label: 'Web',
    value: 'applications',
  },
  {
    label: 'Code',
    value: 'code',
  },
  {
    label: 'Internal',
    value: 'internal',
  },
  {
    label: 'Cloud',
    value: 'cloud',
  },
  {
    label: 'Users',
    value: 'users',
  },
  {
    label: 'Device',
    value: 'device',
  },
];

export const SUBSCRIPTION_TIERS: SubscriptionTier[] = [
  {
    id: 'freemium',
    displayName: 'Freemium',
    features: {
      'Product Modules': {
        'Attack Surface Management': true,
        'Unified Vulnerability Management': true,
        'Continuous Penetration Testing': false,
        'Cyber Threat Intelligence': false,
        'Application Security Testing': false,
        'Adversary Emulation Validation': false,
        'Red Teaming': false,
      },
    },
  },
  {
    id: 'foundational',
    displayName: 'Foundational',
    features: {
      'Product Modules': {
        'Attack Surface Management': true,
        'Unified Vulnerability Management': true,
        'Continuous Penetration Testing': true,
        'Cyber Threat Intelligence': true,
        'Application Security Testing': false,
        'Adversary Emulation Validation': false,
        'Red Teaming': false,
      },
    },
  },
  {
    id: 'professional',
    displayName: 'Professional',
    features: {
      'Product Modules': {
        'Attack Surface Management': true,
        'Unified Vulnerability Management': true,
        'Continuous Penetration Testing': true,
        'Cyber Threat Intelligence': true,
        'Application Security Testing': true,
        'Adversary Emulation Validation': false,
        'Red Teaming': false,
      },
    },
  },
  {
    id: 'ultimate',
    displayName: 'Ultimate',
    features: {
      'Product Modules': {
        'Attack Surface Management': true,
        'Unified Vulnerability Management': true,
        'Continuous Penetration Testing': true,
        'Cyber Threat Intelligence': true,
        'Application Security Testing': true,
        'Adversary Emulation Validation': true,
        'Red Teaming': false,
      },
    },
  },
  {
    id: 'ludacris',
    displayName: 'Ludacris',
    features: {
      // Ludacris = Ultimate + Red Teaming (the only tier with a red-team engagement).
      'Product Modules': {
        'Attack Surface Management': true,
        'Unified Vulnerability Management': true,
        'Continuous Penetration Testing': true,
        'Cyber Threat Intelligence': true,
        'Application Security Testing': true,
        'Adversary Emulation Validation': true,
        'Red Teaming': true,
      },
    },
  },
];

export const NO_OF_ASSETS_OPTIONS = [
  { value: 5000, label: '≤ 5K' },
  { value: 25000, label: '≤ 25K' },
  { value: 50000, label: '≤ 50K' },
  { value: 100000, label: '≤ 100K' },
  { value: -1, label: '> 100K (Contact Sales)' },
];

export const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

/**
 * Maps a scan-cadence value to its human-readable label from FREQUENCY_OPTIONS.
 * Falls back to the raw value if no option matches (e.g. legacy/unknown cadences).
 */
export function frequencyLabel(value: string): string {
  return FREQUENCY_OPTIONS.find(o => o.value === value)?.label ?? value;
}

/**
 * Maps a subscription tier id to its display name from SUBSCRIPTION_TIERS.
 * Falls back to the raw id when no tier matches.
 */
export const subscriptionTierDisplayName = (id: string): string =>
  SUBSCRIPTION_TIERS.find(t => t.id === id)?.displayName ?? id;

/** Shared Tailwind class for subscription form inputs */
export const SUBSCRIPTION_INPUT_CLASS =
  'border-chariot-stroke focus:border-chariot-brand-primary focus:ring-chariot-brand-primary bg-chariot-background-primary text-chariot-text-primary w-50 rounded-md border-2 px-4 py-2 transition-all focus:ring-2 focus:ring-offset-1 focus:outline-hidden';

/** Scan frequency type shared across subscription components */
export type ScanFrequency =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'quarterly';

export interface GuardIntelligenceTier {
  id: string; // 'none' | 'centurion' | 'tribune' | 'prefect' | 'legate' | 'imperator'
  code: string; // platform-ladder code: '' | 'PGIC' | 'PGIT' | 'PGIP' | 'PGIL' | 'PGIM'
  name: string; // display name
  annualPrice: number;
}

// FRONTEND source of truth for Intelligence tier prices. The tier id/code SET
// is ALSO encoded in the Go backend (backend/pkg/handler/handlers/configuration/
// intelligence_tier.go catalog and backend/pkg/tabularium/model/subscription.go
// validator), so ADDING/REMOVING a tier requires editing all three layers. The
// Go test TestIntelligenceTierCatalog_MatchesValidator pins the two Go layers
// together; a TS fixture test pins this list.
export const GUARD_INTELLIGENCE_TIERS: readonly GuardIntelligenceTier[] = [
  { id: 'none', code: '', name: 'None / BYOAI', annualPrice: 0 },
  { id: 'centurion', code: 'PGIC', name: 'Centurion', annualPrice: 12000 },
  { id: 'tribune', code: 'PGIT', name: 'Tribune', annualPrice: 30000 },
  { id: 'prefect', code: 'PGIP', name: 'Prefect', annualPrice: 60000 },
  { id: 'legate', code: 'PGIL', name: 'Legate', annualPrice: 90000 },
  { id: 'imperator', code: 'PGIM', name: 'Imperator', annualPrice: 120000 },
] as const;

export type IntelligenceTierId =
  (typeof GUARD_INTELLIGENCE_TIERS)[number]['id'];

/** Legacy aiCreditPack id -> nearest Intelligence tier id; the resolved id IS persisted on the next save, so reads must be id/code-tolerant. */
export const LEGACY_AI_PACK_TO_TIER: Record<string, IntelligenceTierId> = {
  none: 'none',
  starter: 'centurion', // $6000 legacy -> nearest paid named tier ($12000 floor)
  analyst: 'centurion', // $12000 == Centurion
  operator: 'tribune', // $30000 == Tribune
  maestro: 'prefect', // $60000 == Prefect
};

/** Monthly display budget derived from annual (backend authors the real value). */
export const intelligenceMonthlyBudget = (annual: number): number =>
  Math.round(annual / 12);

/**
 * Look up a Guard Intelligence tier by its id. Returns the 'none' sentinel
 * (annualPrice 0, name 'None / BYOAI') for unknown ids so callers never deal
 * with undefined. Pass an ALREADY-RESOLVED id — call resolveIntelligenceTierId()
 * first if you have a legacy aiCreditPack or a platform code (PGIC…), since this
 * matches on `id` only.
 */
export function getIntelligenceTier(id: string): GuardIntelligenceTier {
  return (
    GUARD_INTELLIGENCE_TIERS.find(t => t.id === id) ??
    GUARD_INTELLIGENCE_TIERS[0]
  );
}

/** Resolve a subscription's effective tier id for DISPLAY (new field wins, legacy falls back). */
export function resolveIntelligenceTierId(sub: {
  intelligenceTier?: string;
  aiCreditPack?: string;
}): IntelligenceTierId {
  // The new field wins whenever the key carries a real string value (including
  // the explicit ""/none BYOAI sentinel). Only an absent/undefined key falls
  // back to the legacy aiCreditPack — mirrors the backend's
  // Subscription.IntelligenceTier() present/absent semantics.
  if (sub.intelligenceTier !== undefined) {
    if (sub.intelligenceTier === '' || sub.intelligenceTier === 'none') {
      return 'none';
    }
    const match = GUARD_INTELLIGENCE_TIERS.find(
      t => t.id === sub.intelligenceTier || t.code === sub.intelligenceTier
    );
    return match?.id ?? 'none';
  }
  if (sub.aiCreditPack)
    return LEGACY_AI_PACK_TO_TIER[sub.aiCreditPack] ?? 'none';
  return 'none';
}

export const COST_WINDOWS = [
  { label: '1d', days: 1 },
  { label: '7d', days: 7 },
  { label: '28d', days: 28 },
  { label: '90d', days: 90 },
] as const;

export function presetRange(
  days: number
): { from: string; to: string } | undefined {
  if (days < 1) return undefined;
  const to = new Date();
  const from = new Date(to);
  if (days > 1) from.setDate(from.getDate() - days);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}
